package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationTypes
import com.siriusxm.cmc.utils.getString
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.CloudEvent
import unifiedcontent.aggregator.CloudEventList
import unifiedcontent.aggregator.ContentUpdateError
import unifiedcontent.aggregator.EventSearchStatus
import unifiedcontent.aggregator.EventSearchStatusState
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * A group of cloud events that share the same error reason.
 */
data class EventGroup(
    val reason: String,
    val events: List<CloudEvent>,
    val entityTypes: Set<String>,
    val sources: Set<String>
) {
    val count get() = events.size
    val lastSeen get() = events.maxOf { it.time }
    val firstSeen get() = events.minOf { it.time }
}

/**
 * Extract the error reason from an invalid content update cloud event.
 */
fun CloudEvent.errorReason(): String? {
    return data?.let {
        try {
            cloudEventDecoder.decodeFromJsonElement(ContentUpdateError.serializer(), it)
        } catch (_: Throwable) {
            null
        }
    }?.reason
}

/**
 * Extract the entity type from the update payload within an invalid content update event.
 */
fun CloudEvent.errorEntityType(): String? {
    val eventData = data?.jsonObject ?: return null
    val updatePayload = (eventData["update"] ?: eventData["partial"]) as? JsonObject ?: return null
    val entity = (updatePayload["entity"] ?: updatePayload["modification"]) as? JsonObject ?: return null
    return entity.entityType
}

/**
 * Extract the source producer id from an invalid content update event.
 */
fun CloudEvent.errorSource(): String? {
    return data?.jsonObject?.getString("source")
}

/**
 * Group a list of cloud events by their error reason.
 * When [groupByProducer] is true, events are further split by source producer
 * so each producer gets its own group for the same error reason.
 * Groups are sorted by count descending.
 */
fun List<CloudEvent>.groupByErrorReason(groupByProducer: Boolean = false): List<EventGroup> {
    return groupBy { event ->
        val reason = event.errorReason() ?: "Unknown"
        val source = if (groupByProducer) event.errorSource() else null
        reason to source
    }
        .map { (k, events) ->
            EventGroup(
                reason = k.first,
                events = events.sortedByDescending { it.time },
                entityTypes = events.mapNotNull { it.errorEntityType() }.toSet(),
                sources = events.mapNotNull { it.errorSource() }.toSet()
            )
        }
        .sortedByDescending { it.count }
}

/**
 * Filter event groups by a search query (case-insensitive partial match on reason).
 */
fun List<EventGroup>.filterBySearchQuery(query: String): List<EventGroup> {
    if (query.isBlank()) return this
    val lowerQuery = query.lowercase()
    return filter { it.reason.lowercase().contains(lowerQuery) }
}

object GroupedEventsStyles : StyleSheet() {
    val searchInput by style {
        flex(1)
        padding(8.px, 12.px)
        fontSize(14.px)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(SXMUI.boxRadius.value())
        property("outline", "none")
        color(SXMUI.textColor.value())
    }

    val metaTimestamp by style {
        fontSize(0.9.em)
        fontStyle("italic")
        color(SXMUI.subtleTextColor.value())
    }

}

/**
 * A search bar for filtering grouped events by error message.
 *
 * Two callbacks rather than one because the value has two audiences with different tolerances for
 * churn. [onQueryChange] fires per keystroke and drives the on-screen filtering, so typing stays
 * live. [onCommit] fires only on Enter or when the field loses focus, and is where a caller that
 * mirrors the search into the URL should do it — a push per keystroke would put one history entry
 * behind every letter of the word being typed.
 *
 * @param query the current text to display
 * @param onQueryChange called on every keystroke
 * @param onCommit called with the current text on Enter or blur
 */
@Composable
fun EventsSearchBar(
    query: String,
    onQueryChange: (String) -> Unit,
    onCommit: (String) -> Unit = {}
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(8.px, 0.px)
        }
    }) {
        Input(InputType.Text) {
            classes(GroupedEventsStyles.searchInput)
            value(query)
            placeholder("Filter by error message...")
            onInput { onQueryChange(it.value) }
            onKeyDown { if (it.key == "Enter") onCommit(query) }
            onBlur { onCommit(query) }
        }
    }
}

/**
 * Renders event groups in an expandable table. Expanding a group shows
 * the individual events via [cloudEventTable].
 */
@Composable
fun GroupedEventTable(
    aggregator: AggregatorService,
    groups: List<EventGroup>,
    showProducer: Boolean = false
) {
    table<EventGroup> {
        items(groups)
        expandable { group ->
            cloudEventTable(aggregator, group.events)
        }
        column {
            content { group ->
                Div({
                    classes(CloudEventTable.eventSummary)
                    style { gap(4.px) }
                }) {
                    Span({ style { fontWeight("bold") } }) {
                        Text(group.reason)
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            alignItems(AlignItems.Center)
                            flexWrap(FlexWrap.Wrap)
                        }
                    }) {
                        Badge(group.count.toString(), BadgeColor.RED)

                        if (showProducer && group.sources.isNotEmpty()) {
                            Span({ style { fontWeight("bold") } }) {
                                val producerNames = group.sources.map { source ->
                                    aggregator.producerById(source)?.name ?: source
                                }
                                Text("Producer: ${producerNames.joinToString(", ")}")
                            }
                        }

                        if (group.entityTypes.isNotEmpty()) {
                            Span({ style { fontWeight("bold") } }) {
                                val typeNames = group.entityTypes.map { type ->
                                    aggregator.entityType(type)?.name ?: type
                                }
                                Text("Type: ${typeNames.joinToString(", ")}")
                            }
                        }

                        // Render in the globally-selected timezone (header picker).
                        Span({ classes(GroupedEventsStyles.metaTimestamp) }) {
                            Text("Last: ${group.lastSeen.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()}")
                        }

                        Span({ classes(GroupedEventsStyles.metaTimestamp) }) {
                            Text("First: ${group.firstSeen.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString()}")
                        }
                    }
                }
            }
        }
    }
}

/**
 * The maximum number of events the API will return in a single query.
 * When results hit this limit, a truncation warning is shown.
 */
private const val API_EVENT_LIMIT = 1000

/**
 * A grouped view of invalid events. Events are grouped by error reason with counts
 * and metadata. Each group expands to show individual events. A search bar filters
 * groups by error message.
 *
 * The view is **controlled**: it holds no filter state, and hands every change to [onFiltersChange].
 * The producer detail page passes filters decoded from the URL and navigates on change, so a filtered
 * error list is shareable (UCA-1028 follow-up); `ProducersDashboard` passes plain `remember`ed state
 * and so writes nothing to the URL. Keeping that choice at the call site rather than behind a flag is
 * why there is no "sync to URL" parameter here.
 *
 * @param aggregator the aggregator service for lookups
 * @param showProducer whether to show producer names in group metadata
 * @param filters the current time range and error-message search. Only [EventFilters.range] triggers
 *   a fetch; the search filters the loaded groups client-side.
 * @param onFiltersChange called with the new selections when the range changes, or when a search is
 *   committed with Enter or blur
 * @param query the API query lambda
 */
@Composable
fun GroupedInvalidEvents(
    aggregator: AggregatorService,
    showProducer: Boolean = false,
    filters: EventFilters,
    onFiltersChange: (EventFilters) -> Unit,
    query: suspend (range: TimeRange?, queryId: String?) -> EventSearchStatus
) {
    var loading by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<EventSearchStatus?>(null) }
    var events by remember { mutableStateOf<CloudEventList?>(null) }
    var failureMessage by remember { mutableStateOf<String?>(null) }

    val eventsPeriod = filters.range

    // The search box keeps a draft so that filtering stays live per keystroke while the committed
    // value — the one that reaches the URL — only changes on Enter or blur. Reseeded from the
    // committed value so that Back, a reload, and a pasted link all put the right text in the box.
    var searchDraft by remember { mutableStateOf(filters.search.orEmpty()) }
    LaunchedEffect(filters.search) { searchDraft = filters.search.orEmpty() }

    Style(EntityEventsStyles)
    Style(GroupedEventsStyles)

    // Note there is no `searchQuery = ""` here any more. Clearing the search on every range change
    // would make `?range=1d&search=duplicate` unloadable — the fetch would complete and immediately
    // discard the search the link asked for. Dropped for both call sites rather than only the
    // URL-backed one: the same component behaving differently on two pages is worse than the small
    // change, and the "No error groups match your search." empty state already explains an empty
    // table after a range change.
    LaunchedEffect(eventsPeriod) {
        val range = eventsPeriod?.range()
        if (eventsPeriod != null && eventsPeriod.isOutsideEventRetention()) {
            // A range from a link or bookmark can predate retention; don't send a search the
            // backend can only reject (UCA-1168). Reset loading here too: a range change cancels
            // an in-flight search, so the previous run may never have cleared it.
            loading = false
            result = null
            events = null
            failureMessage = EVENT_RETENTION_MESSAGE
        } else if (range != null) {
            loading = true
            // Cleared here so a retention (or earlier) failure doesn't outlive the range it was for.
            failureMessage = null
            events = null
            try {
                result = loadAccumulatedEvents({ queryId ->
                    query(eventsPeriod, queryId)
                }) {
                    events = it
                }
                if (result?.state == EventSearchStatusState.FAILED) {
                    failureMessage = "We were unable to load the events"
                }
            } catch (e: CancellationException) {
                // A range change cancels the previous LaunchedEffect run mid-poll; that's a normal
                // supersession, not a failed search, and must propagate so the coroutine actually
                // stops rather than reporting a spurious error over the newer run's state.
                throw e
            } catch (t: Throwable) {
                failureMessage = t.message ?: "We were unable to load the events"
            } finally {
                // In finally so a cancelled run still clears it; otherwise a range change mid-poll
                // could leave "Loading events..." showing indefinitely.
                loading = false
            }
        }
    }

    eventsSelectionHeader(
        loading, selectedTimeRange = eventsPeriod
    ) { range, _, _ ->
        onFiltersChange(filters.copy(range = range))
    }

    val resultEvents = events.orEmpty()
    val allGroups = remember(resultEvents) { resultEvents.groupByErrorReason(groupByProducer = showProducer) }
    // Filter on the draft, not the committed value, so the table narrows as the user types.
    val filteredGroups = remember(allGroups, searchDraft) { allGroups.filterBySearchQuery(searchDraft) }

    box({
        paddedContent = false
    }) {
        if (failureMessage != null) {
            boxMessage(failureMessage!!)
        } else if (eventsPeriod == null) {
            boxMessage("Please select a time period.")
        }

        if (resultEvents.isNotEmpty()) {
            boxContent {
                EventsSearchBar(
                    query = searchDraft,
                    onQueryChange = { searchDraft = it },
                    onCommit = { committed ->
                        val normalised = committed.takeIf { it.isNotBlank() }
                        if (normalised != filters.search) {
                            onFiltersChange(filters.copy(search = normalised))
                        }
                    }
                )

                if (resultEvents.size >= API_EVENT_LIMIT) {
                    NotificationBox(
                        Notification(
                            NotificationTypes.Warning,
                            "Showing $API_EVENT_LIMIT events (API limit). Some events may not be included. Try a shorter time range for complete results.",
                            null
                        )
                    )
                }
            }

            if (filteredGroups.isNotEmpty()) {
                GroupedEventTable(aggregator, filteredGroups, showProducer)
            } else {
                boxMessage("No error groups match your search.")
            }
        } else {
            if (!loading && eventsPeriod != null && failureMessage == null) {
                boxMessage("No invalid producer events occurred in this period.")
            }
        }
    }
}
